/** TicketsPage — Kanban K-B (Tabular Density). Pixel-perfect del mockup. */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger,
  DropdownMenuSubContent, DropdownMenuTrigger, DropdownMenuPortal,
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Ticket as TicketIcon, Search, Loader2,
  Mail, Phone, Globe, MessageCircle, User as UserIcon,
  RefreshCw, AlertCircle, X, Plus, Filter, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext, PointerSensor, useSensor, useSensors, useDroppable, useDraggable,
  DragOverlay, closestCenter,
} from '@dnd-kit/core';
import { useAuth } from '../../context/AuthContext';
import { getSlaState } from './components/slaIndicator';
import TicketPresencialModal from './components/TicketPresencialModal';
import './tickets-theme.css';

const ESTADOS = [
  { key: 'NUEVO',      label: 'Nuevo' },
  { key: 'ASIGNADO',   label: 'Asignado' },
  { key: 'EN_PROCESO', label: 'En proceso' },
  { key: 'TERMINADO',  label: 'Terminado' },
];

/** Mapa de transiciones permitidas (espejo del backend ticket_router.py). */
const TRANSICIONES_VALIDAS = {
  NUEVO:      new Set(['ASIGNADO', 'EN_PROCESO', 'TERMINADO']),
  ASIGNADO:   new Set(['EN_PROCESO', 'TERMINADO']),
  EN_PROCESO: new Set(['TERMINADO']),
  TERMINADO:  new Set(['EN_PROCESO']),
};

const ICONOS_CANAL = { Mail, Phone, Globe, MessageCircle, User: UserIcon };

const initialsOf = (s) => {
  if (!s) return '?';
  const parts = s.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};
const colorForString = (s) => {
  if (!s) return '#94a3b8';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h}, 45%, 55%)`;
};

/** Indicador sutil "Actualizado hace Xs" con dot pulsante. */
function RefreshIndicator({ lastUpdatedAt, refreshing, tick }) {
  if (!lastUpdatedAt) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - lastUpdatedAt) / 1000));
  let label;
  if (refreshing) label = 'Actualizando…';
  else if (diffSec < 5) label = 'Actualizado ahora';
  else if (diffSec < 60) label = `Actualizado hace ${diffSec}s`;
  else {
    const m = Math.floor(diffSec / 60);
    label = `Actualizado hace ${m} min`;
  }
  // tick unused but forces re-render; reference for lint
  void tick;
  return (
    <span className="tk-refresh-indicator" data-testid="tk-refresh-indicator" aria-live="polite">
      <span className={`tk-refresh-dot ${refreshing ? 'is-refreshing' : ''}`} />
      {label}
    </span>
  );
}

export default function TicketsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { empresa, api } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [prioridades, setPrioridades] = useState([]);
  const [canales, setCanales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroResp, setFiltroResp] = useState('all');
  const [filtroPri, setFiltroPri] = useState('all');
  const [filtroCanal, setFiltroCanal] = useState('all');

  const filtroEstado = searchParams.get('estado');
  const filtroSla = searchParams.get('sla');
  const limpiarFiltrosURL = () => setSearchParams({});

  // ============ Drag & Drop ============
  const [activeDrag, setActiveDrag] = useState(null);   // ticket que está siendo arrastrado
  const [assignModal, setAssignModal] = useState(null); // { ticket, destinoEstado, responsableId, prioridadId }
  const [kioskOpen, setKioskOpen] = useState(false);   // modal "Nuevo ticket presencial"
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0); // re-render para "hace Xs"
  const activeDragRef = useRef(null);
  const assignModalRef = useRef(null);
  useEffect(() => { activeDragRef.current = activeDrag; }, [activeDrag]);
  useEffect(() => { assignModalRef.current = assignModal; }, [assignModal]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const patchTicket = async (ticketId, payload, prevTickets) => {
    try {
      const { data } = await api.patch(`/tickets/${ticketId}`, payload, {
        headers: { 'X-Tk-Source': 'kanban' },
      });
      setTickets((arr) => arr.map((t) => (t.id === ticketId ? { ...t, ...data } : t)));
      toast.success('Ticket actualizado');
      return true;
    } catch (e) {
      // Rollback optimistic
      if (prevTickets) setTickets(prevTickets);
      const detail = e?.response?.data?.detail || 'No se pudo actualizar el ticket';
      toast.error(typeof detail === 'string' ? detail : 'No se pudo actualizar');
      return false;
    }
  };

  const onDragStart = (event) => {
    const t = tickets.find((x) => x.id === event.active.id);
    setActiveDrag(t || null);
  };

  const onDragEnd = async (event) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;
    const ticket = tickets.find((x) => x.id === active.id);
    const destino = over.id;
    if (!ticket || ticket.estado === destino) return;

    // Validar transición (espejo del backend)
    if (!TRANSICIONES_VALIDAS[ticket.estado]?.has(destino)) {
      toast.error(`Transición no permitida: ${ticket.estado} → ${destino}`);
      return;
    }

    // Pre-requisitos al salir de NUEVO
    if (ticket.estado === 'NUEVO' && (destino === 'ASIGNADO' || destino === 'EN_PROCESO')) {
      const faltaResp = !ticket.responsable_id;
      const faltaPri = !ticket.prioridad_id;
      if (faltaResp || faltaPri) {
        setAssignModal({
          ticket,
          destinoEstado: destino,
          responsableId: ticket.responsable_id || '',
          prioridadId: ticket.prioridad_id || '',
        });
        return;
      }
    }

    // Optimistic update
    const prev = tickets;
    setTickets((arr) => arr.map((t) => (t.id === ticket.id ? { ...t, estado: destino } : t)));
    await patchTicket(ticket.id, { estado: destino }, prev);
  };

  const onAssignModalSave = async () => {
    if (!assignModal) return;
    const { ticket, destinoEstado, responsableId, prioridadId } = assignModal;
    if (!responsableId || !prioridadId) {
      toast.error('Debes seleccionar responsable y prioridad');
      return;
    }
    const prev = tickets;
    setTickets((arr) => arr.map((t) => (
      t.id === ticket.id ? { ...t, responsable_id: responsableId, prioridad_id: prioridadId, estado: destinoEstado } : t
    )));
    const ok = await patchTicket(ticket.id, {
      responsable_id: responsableId,
      prioridad_id: prioridadId,
      estado: destinoEstado,
    }, prev);
    if (ok) setAssignModal(null);
  };

  const fetchAll = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      if (silent) {
        // Refresh silencioso: solo tickets (catálogos casi estáticos)
        const tRes = await api.get('/tickets');
        setTickets(tRes.data || []);
      } else {
        const [tRes, rRes, pRes, cRes] = await Promise.all([
          api.get('/tickets'),
          api.get('/tickets/responsables'),
          api.get('/tickets/prioridades'),
          api.get('/tickets/canales'),
        ]);
        setTickets(tRes.data || []);
        setResponsables(rRes.data || []);
        setPrioridades(pRes.data || []);
        setCanales(cRes.data || []);
      }
      setLastUpdatedAt(Date.now());
    } catch (e) {
      if (!silent) console.error('Error cargando tickets:', e);
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    if (empresa?.modulo_tickets_activo) fetchAll();
    else setLoading(false);
    // eslint-disable-next-line
  }, []);

  // Auto-refresh cada 30s mientras la pestaña esté activa.
  // Pausa cuando: pestaña oculta, drag activo, modal de asignación abierto, o carga inicial en curso.
  useEffect(() => {
    if (!empresa?.modulo_tickets_activo) return undefined;
    const POLL_MS = 30_000;
    let timer = null;
    const tryRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (activeDragRef.current) return;
      if (assignModalRef.current) return;
      fetchAll({ silent: true });
    };
    const start = () => {
      if (timer) return;
      timer = setInterval(tryRefresh, POLL_MS);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line
  }, [empresa?.modulo_tickets_activo]);

  // Re-render del indicador "hace Xs" cada 5s
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 5_000);
    return () => clearInterval(id);
  }, []);

  const filteredTickets = useMemo(() => {
    let arr = tickets;
    if (filtroResp !== 'all') {
      arr = filtroResp === 'unassigned'
        ? arr.filter((t) => !t.responsable_id)
        : arr.filter((t) => t.responsable_id === filtroResp);
    }
    if (filtroPri !== 'all') arr = arr.filter((t) => t.prioridad_id === filtroPri);
    if (filtroCanal !== 'all') arr = arr.filter((t) => t.canal_id === filtroCanal);
    if (filtroEstado === 'ABIERTO') arr = arr.filter((t) => t.estado !== 'TERMINADO');
    else if (filtroEstado) arr = arr.filter((t) => t.estado === filtroEstado);
    if (filtroSla === 'incumplido') arr = arr.filter((t) => t.sla_cumplido === false);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((t) =>
        (t.codigo || '').toLowerCase().includes(q)
        || (t.cliente_email || '').toLowerCase().includes(q)
        || (t.asunto_original || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [tickets, search, filtroResp, filtroPri, filtroCanal, filtroEstado, filtroSla]);

  const estadosVisibles = useMemo(() => {
    if (!filtroEstado) return ESTADOS;
    if (filtroEstado === 'ABIERTO') return ESTADOS.filter((e) => e.key !== 'TERMINADO');
    return ESTADOS.filter((e) => e.key === filtroEstado);
  }, [filtroEstado]);

  const grupos = useMemo(() => {
    const g = { NUEVO: [], ASIGNADO: [], EN_PROCESO: [], TERMINADO: [] };
    for (const t of filteredTickets) if (g[t.estado]) g[t.estado].push(t);
    return g;
  }, [filteredTickets]);

  const slaRatioPorEstado = useMemo(() => {
    const out = {};
    for (const key of Object.keys(grupos)) {
      const lista = grupos[key];
      const evaluados = lista.filter((t) => t.sla_cumplido !== null && t.sla_cumplido !== undefined);
      if (evaluados.length === 0) {
        const enRiesgo = lista.filter((t) => {
          const s = getSlaState(t);
          return s && (s.key === 'WARN' || s.key === 'VENCIDO');
        }).length;
        out[key] = {
          pct: lista.length > 0 ? Math.round(((lista.length - enRiesgo) / lista.length) * 100) : null,
          mode: 'riesgo',
          total: lista.length,
          enRiesgo,
        };
      } else {
        const ok = evaluados.filter((t) => t.sla_cumplido).length;
        out[key] = {
          pct: Math.round((ok / evaluados.length) * 100),
          mode: 'cumplido',
          total: lista.length,
          evaluados: evaluados.length,
          ok,
          fallidos: evaluados.length - ok,
        };
      }
    }
    return out;
  }, [grupos]);

  if (!empresa?.modulo_tickets_activo) {
    return (
      <div className="tickets-scope tk-shell">
        <div style={{ background: 'var(--tk-surface)', border: '1px solid var(--tk-border)', borderRadius: 10, padding: 40, textAlign: 'center', maxWidth: 720, margin: '40px auto' }}>
          <AlertCircle style={{ width: 38, height: 38, color: 'var(--tk-amber)', margin: '0 auto 12px' }} />
          <p style={{ fontWeight: 500, color: 'var(--tk-text)' }}>El módulo Centro de Ayuda no está activado.</p>
        </div>
      </div>
    );
  }

  // Chips de filtros activos
  const chips = [];
  if (filtroResp !== 'all') {
    const label = filtroResp === 'unassigned' ? 'Sin asignar' : (responsables.find((r) => r.id === filtroResp)?.nombre || 'Responsable');
    chips.push({ key: 'resp', label: `Responsable: ${label}`, clear: () => setFiltroResp('all') });
  }
  if (filtroPri !== 'all') {
    const p = prioridades.find((x) => x.id === filtroPri);
    chips.push({ key: 'pri', label: `Prioridad: ${p?.nombre || filtroPri}`, color: p?.color, clear: () => setFiltroPri('all') });
  }
  if (filtroCanal !== 'all') {
    const c = canales.find((x) => x.id === filtroCanal);
    chips.push({ key: 'canal', label: `Canal: ${c?.nombre || filtroCanal}`, clear: () => setFiltroCanal('all') });
  }
  if (filtroEstado) chips.push({ key: 'estado', label: `Vista: ${filtroEstado === 'ABIERTO' ? 'Abiertos' : filtroEstado}`, clear: limpiarFiltrosURL, testid: 'btn-limpiar-kpi-filter' });
  if (filtroSla === 'incumplido') chips.push({ key: 'sla', label: 'Vista: SLA incumplido', clear: limpiarFiltrosURL });

  const gridCols = estadosVisibles.length === 1 ? '1fr'
    : estadosVisibles.length === 2 ? 'repeat(2, 1fr)'
    : estadosVisibles.length === 3 ? 'repeat(3, 1fr)'
    : 'repeat(4, 1fr)';

  return (
    <div className="tickets-scope tk-shell">
      {/* Header */}
      <div className="tk-view-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 6 }}>
        <div>
          <h1 style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <TicketIcon style={{ width: 20, height: 20 }} />
            Tablero de Tickets
          </h1>
          <div className="tk-sub" style={{ marginTop: 2 }}>
            {filteredTickets.length} {filteredTickets.length === 1 ? 'ticket visible' : 'tickets visibles'}
            {chips.length > 0 && ' · filtros aplicados'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <RefreshIndicator lastUpdatedAt={lastUpdatedAt} refreshing={refreshing} tick={tick} />
          <button className="tk-btn-toolbar" onClick={() => fetchAll()} disabled={loading} data-testid="btn-refresh-tickets">
            <RefreshCw style={{ width: 13, height: 13, marginRight: 5, display: 'inline', animation: (loading || refreshing) ? 'spin 1s linear infinite' : 'none' }} />
            Refrescar
          </button>
        </div>
      </div>

      {/* TOOLBAR plano (Mockup K-B) */}
      <div className="tk-toolbar-flat">
        <div className="tk-search">
          <Search style={{ width: 13, height: 13, color: 'var(--tk-text-soft)' }} />
          <input
            placeholder="Buscar por código, cliente o asunto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-tickets"
          />
        </div>

        {chips.length > 0 && (
          <>
            <span className="tk-toolbar-label-inline">Filtros activos:</span>
            {chips.map((c) => (
              <span key={c.key} className="tk-chip" data-testid={c.testid || `chip-filtro-${c.key}`}>
                {c.color && <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.color }} />}
                {c.label}
                <span
                  className="tk-x"
                  onClick={c.clear}
                  role="button"
                  aria-label={`Quitar filtro ${c.key}`}
                  data-testid={`chip-filtro-${c.key}-remove`}
                >
                  <X style={{ width: 11, height: 11 }} />
                </span>
              </span>
            ))}
          </>
        )}

        {/* + Más filtros (Dropdown con submenús) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="tk-btn-ghost" data-testid="btn-mas-filtros" type="button">
              <Plus style={{ width: 12, height: 12 }} /> Más filtros
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>Agregar filtro</DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="mf-responsable">Responsable</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="max-h-72 overflow-auto">
                  <DropdownMenuItem onSelect={() => setFiltroResp('unassigned')} data-testid="mf-resp-unassigned">Sin asignar</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {responsables.map((r) => (
                    <DropdownMenuItem key={r.id} onSelect={() => setFiltroResp(r.id)} data-testid={`mf-resp-${r.id}`}>
                      {r.nombre}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="mf-prioridad">Prioridad</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  {prioridades.map((p) => (
                    <DropdownMenuItem key={p.id} onSelect={() => setFiltroPri(p.id)} data-testid={`mf-pri-${p.id}`}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, marginRight: 8, display: 'inline-block' }} />
                      {p.nombre}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="mf-canal">Canal</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  {canales.map((c) => (
                    <DropdownMenuItem key={c.id} onSelect={() => setFiltroCanal(c.id)} data-testid={`mf-canal-${c.id}`}>
                      {c.nombre}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>

            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="mf-sla">Estado SLA</DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <DropdownMenuItem onSelect={() => setSearchParams({ ...Object.fromEntries(searchParams), sla: 'incumplido' })} data-testid="mf-sla-incumplido">
                    SLA incumplido
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setSearchParams({ ...Object.fromEntries(searchParams), estado: 'ABIERTO' })} data-testid="mf-estado-abiertos">
                    Sólo abiertos
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setSearchParams({ ...Object.fromEntries(searchParams), estado: 'TERMINADO' })} data-testid="mf-estado-terminados">
                    Sólo terminados
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          </DropdownMenuContent>
        </DropdownMenu>

        <div style={{ flex: 1 }} />

        {/* CTA "+ Nuevo" — abre modal kiosk presencial */}
        <button
          className="tk-btn-cta"
          onClick={() => setKioskOpen(true)}
          title="Registrar nuevo ticket presencial"
          data-testid="btn-nuevo-ticket"
          type="button"
        >
          <Plus style={{ width: 13, height: 13 }} /> Nuevo
        </button>
      </div>

      {/* KANBAN */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
          <Loader2 style={{ width: 32, height: 32, color: 'var(--tk-text-soft)', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="tk-kanban" style={{ gridTemplateColumns: gridCols, flex: 1 }}>
            {estadosVisibles.map((est) => {
              const lista = grupos[est.key] || [];
              const slaInfo = slaRatioPorEstado[est.key];
              const pct = slaInfo?.pct;
              const isWarn = pct !== null && pct !== undefined && pct < 70;
              const accepts = activeDrag
                ? (est.key === activeDrag.estado || TRANSICIONES_VALIDAS[activeDrag.estado]?.has(est.key))
                : true;
              return (
                <KanbanColumn
                  key={est.key}
                  estado={est}
                  lista={lista}
                  pct={pct}
                  slaInfo={slaInfo}
                  isWarn={isWarn}
                  accepts={accepts}
                  responsables={responsables}
                  prioridades={prioridades}
                  canales={canales}
                  onCardClick={(t) => navigate(`/admin/centro-ayuda/tickets/${t.id}`)}
                />
              );
            })}
          </div>

          <DragOverlay>
            {activeDrag ? (
              <div style={{ transform: 'rotate(2deg)', opacity: 0.92 }}>
                <TicketRow
                  ticket={activeDrag}
                  responsables={responsables}
                  prioridades={prioridades}
                  canales={canales}
                  onClick={() => {}}
                  isOverlay
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Modal: faltan datos para mover desde NUEVO */}
      <Dialog open={!!assignModal} onOpenChange={(o) => !o && setAssignModal(null)}>
        <DialogContent data-testid="dialog-asignar-pre-move">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-amber-600" />
              Asignación requerida
            </DialogTitle>
            <DialogDescription>
              Para mover el ticket <strong>{assignModal?.ticket?.codigo}</strong> a{' '}
              <strong>{assignModal?.destinoEstado === 'ASIGNADO' ? 'Asignado' : 'En proceso'}</strong>{' '}
              necesitas asignar un responsable y una prioridad. Si cancelas, el ticket permanecerá en estado <strong>Nuevo</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 my-2">
            <div>
              <Label>Responsable</Label>
              <Select
                value={assignModal?.responsableId || ''}
                onValueChange={(v) => setAssignModal((m) => m && { ...m, responsableId: v })}
              >
                <SelectTrigger data-testid="select-modal-responsable">
                  <SelectValue placeholder="Seleccione un responsable" />
                </SelectTrigger>
                <SelectContent>
                  {responsables.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prioridad</Label>
              <Select
                value={assignModal?.prioridadId || ''}
                onValueChange={(v) => setAssignModal((m) => m && { ...m, prioridadId: v })}
              >
                <SelectTrigger data-testid="select-modal-prioridad">
                  <SelectValue placeholder="Seleccione una prioridad" />
                </SelectTrigger>
                <SelectContent>
                  {prioridades.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nombre} ({p.horas_sla}h)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignModal(null)} data-testid="btn-asignar-cancelar">
              Cancelar
            </Button>
            <Button
              onClick={onAssignModalSave}
              disabled={!assignModal?.responsableId || !assignModal?.prioridadId}
              data-testid="btn-asignar-guardar"
            >
              Asignar y mover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal kiosk presencial */}
      <TicketPresencialModal
        open={kioskOpen}
        onClose={() => setKioskOpen(false)}
        onCreated={() => fetchAll({ silent: true })}
      />
    </div>
  );
}

/** Columna del Kanban: zona droppable. */
function KanbanColumn({ estado, lista, pct, slaInfo, isWarn, accepts, responsables, prioridades, canales, onCardClick }) {
  const { setNodeRef, isOver } = useDroppable({ id: estado.key });
  const cls = ['tk-column', isOver && accepts ? 'is-over' : '', isOver && !accepts ? 'is-invalid' : ''].filter(Boolean).join(' ');

  // Tooltip explicativo del % SLA según el modo (riesgo para estados activos,
  // cumplimiento histórico para TERMINADO).
  let slaTooltip;
  if (slaInfo && pct !== null && pct !== undefined) {
    if (slaInfo.mode === 'cumplido') {
      slaTooltip = slaInfo.evaluados === 0
        ? 'Sin tickets evaluados todavía.'
        : `Cumplimiento de SLA al cierre: ${slaInfo.ok} de ${slaInfo.evaluados} ticket(s) cerrados dentro del plazo (${pct}%). Fallidos: ${slaInfo.fallidos}.`;
    } else {
      const sanos = Math.max(0, slaInfo.total - slaInfo.enRiesgo);
      slaTooltip = slaInfo.total === 0
        ? 'Sin tickets en este estado.'
        : `Salud del SLA: ${sanos} de ${slaInfo.total} ticket(s) dentro de su tiempo objetivo (${pct}%). En riesgo o vencidos: ${slaInfo.enRiesgo}.`;
    }
  }

  return (
    <div ref={setNodeRef} className={cls} data-testid={`col-${estado.key}`}>
      <div className="tk-col-header">
        <div className="tk-col-title-row">
          <span className="tk-col-title">{estado.label} · <b>{String(lista.length).padStart(2, '0')}</b></span>
          {pct !== null && pct !== undefined && (
            <span
              className="tk-col-stats"
              style={{ color: isWarn ? 'var(--tk-amber)' : 'var(--tk-green)', cursor: slaTooltip ? 'help' : undefined }}
              title={slaTooltip}
              aria-label={slaTooltip}
              data-testid={`col-${estado.key}-sla`}
            >
              {pct}% SLA
            </span>
          )}
        </div>
        <div className={`tk-progress-bar ${isWarn ? 'tk-progress-warn' : ''}`}>
          <div className="tk-progress-fill" style={{ width: pct !== null && pct !== undefined ? `${pct}%` : '0%' }} />
        </div>
      </div>
      <div className="tk-col-body" style={{ maxHeight: 'calc(100vh - 280px)' }}>
        {lista.length === 0 ? (
          <div style={{ padding: '32px 8px', textAlign: 'center', fontSize: 11, color: 'var(--tk-text-soft)', fontStyle: 'italic' }}>
            Sin tickets
          </div>
        ) : (
          lista.map((t) => (
            <DraggableTicketRow
              key={t.id}
              ticket={t}
              responsables={responsables}
              prioridades={prioridades}
              canales={canales}
              onClick={() => onCardClick(t)}
            />
          ))
        )}
      </div>
    </div>
  );
}

/** Wrapper Draggable que envuelve a TicketRow. */
function DraggableTicketRow({ ticket, onClick, ...rest }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: ticket.id });
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ opacity: isDragging ? 0.4 : 1, touchAction: 'none' }}
    >
      <TicketRow ticket={ticket} onClick={onClick} {...rest} />
    </div>
  );
}

function TicketRow({ ticket, responsables, prioridades, canales, onClick, isOverlay = false }) {
  const resp = responsables.find((r) => r.id === ticket.responsable_id);
  const pri = prioridades.find((p) => p.id === ticket.prioridad_id);
  const canal = canales.find((c) => c.id === ticket.canal_id);
  const Icon = canal ? (ICONOS_CANAL[canal.icono] || Mail) : Mail;
  const sla = getSlaState(ticket);

  const dotClass =
    sla?.key === 'VENCIDO' ? 'tk-dot-late' :
    sla?.key === 'WARN' ? 'tk-dot-warn' :
    sla?.key === 'OK' || sla?.key === 'CUMPLIDO' ? 'tk-dot-ok' :
    sla?.key === 'INCUMPLIDO' ? 'tk-dot-late' : 'tk-dot-none';

  const slaPillClass =
    sla?.key === 'VENCIDO' || sla?.key === 'INCUMPLIDO' ? 'tk-sla-time-late' :
    sla?.key === 'WARN' ? 'tk-sla-time-warn' :
    sla?.key === 'OK' || sla?.key === 'CUMPLIDO' ? 'tk-sla-time-ok' : '';

  const slaText =
    sla?.key === 'CUMPLIDO' ? '✓' :
    sla?.key === 'INCUMPLIDO' ? '✗' :
    sla?.detalle?.replace(/restantes/i, '').replace(/vencido/i, '').trim() || sla?.label;

  return (
    <button onClick={onClick} className="tk-card" data-testid={isOverlay ? undefined : `ticket-card-${ticket.codigo}`} type="button">
      <div className="tk-card-line1">
        <span className="tk-code">{ticket.codigo}</span>
        <span className={`tk-dot-sla ${dotClass}`} title={sla?.label} />
        <span style={{ flex: 1 }} />
        {sla && (
          <span className={`tk-sla-time ${slaPillClass}`} data-testid={`card-sla-${ticket.codigo}`}>{slaText}</span>
        )}
      </div>
      <div className="tk-title-sm">{ticket.asunto_original || '(sin asunto)'}</div>
      <div className="tk-meta-line">
        <Icon style={{ color: canal?.color || 'var(--tk-text-muted)' }} />
        <span
          className="tk-meta-path"
          title={ticket.clasificacion_ruta || canal?.nombre || ''}
          data-testid={ticket.clasificacion_ruta ? `card-clasificacion-${ticket.codigo}` : undefined}
        >
          {ticket.clasificacion_ruta || canal?.nombre || 'Sin clasificar'}
        </span>
        {pri && (
          <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 3, background: `${pri.color}22`, color: pri.color, flexShrink: 0 }} title={`Prioridad: ${pri.nombre}`}>
            {pri.nombre.substring(0, 3).toUpperCase()}
          </span>
        )}
        <span className="tk-iniciales" style={{ background: resp ? colorForString(resp.nombre) : '#cbd5e1' }} title={resp ? resp.nombre : 'Sin asignar'}>
          {resp ? initialsOf(resp.nombre) : '—'}
        </span>
      </div>
    </button>
  );
}
